import { postUploadUrls, putUploadStore } from "@app/api";
import { StorageKey, TYPE_ALLOW } from "@app/constants";
import { delay, removeItem, setItem } from "@app/helpers";

export const download = async (url: string, filename: string) => {
  const link = document.createElement("a");
  link.download = filename;
  link.href = url;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();

  await delay(100);
  document.body.removeChild(link);
};

export const compressImageFile = (
  file: File,
  maxDimension: number,
  quality: number,
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDimension / Math.max(img.width, img.height));
      const width = Math.max(1, Math.round(img.width * scale));
      const height = Math.max(1, Math.round(img.height * scale));

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error("Canvas context not available"));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          if (!blob) {
            reject(new Error("Failed to create blob"));
            return;
          }
          const baseName = file.name.replace(/\.[^/.]+$/, "");
          resolve(new File([blob], `${baseName}.jpeg`, { type: "image/jpeg" }));
        },
        "image/jpeg",
        quality,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };
    img.src = url;
  });
};

export const downloadMultipleFiles = (files: string[]) => {
  files.forEach((file, index) => {
    (async () => {
      const filename = file.substring(file.lastIndexOf("/") + 1);
      await delay(index * 1000);
      download(file, filename);
    })();
  });
};

export const processBlobToCSV = (blobData: string, fileName: string) => {
  const contentType = "text/csv";
  const blob = new Blob([blobData], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const filename = `${fileName}.csv`;
  download(url, filename);
};

export const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
};

const readFileSignature = async (file: File) => {
  const buffer = await file.slice(0, 32).arrayBuffer();
  return new Uint8Array(buffer);
};

const isValidImageSignature = (signature: Uint8Array) => {
  if (signature.length < 4) return false;

  if (
    signature[0] === 0x89 &&
    signature[1] === 0x50 &&
    signature[2] === 0x4e &&
    signature[3] === 0x47
  ) {
    return true;
  }

  if (signature[0] === 0x47 && signature[1] === 0x49 && signature[2] === 0x46) {
    return true;
  }

  if (
    signature[0] === 0x52 &&
    signature[1] === 0x49 &&
    signature[2] === 0x46 &&
    signature[3] === 0x46
  ) {
    return true;
  }

  return (
    signature[0] === 0xff && signature[1] === 0xd8 && signature[2] === 0xff
  );
};

const isValidVideoSignature = (signature: Uint8Array) => {
  if (signature.length < 12) return false;

  return (
    (signature[0] === 0x00 &&
      signature[1] === 0x00 &&
      signature[2] === 0x00 &&
      (signature[3] === 0x18 ||
        signature[3] === 0x1c ||
        signature[3] === 0x20)) ||
    signature.slice(4, 8).toString() === "102,116,121,112"
  );
};

const isValidPdfSignature = (signature: Uint8Array) => {
  if (signature.length < 5) return false;

  return (
    signature[0] === 0x25 &&
    signature[1] === 0x50 &&
    signature[2] === 0x44 &&
    signature[3] === 0x46 &&
    signature[4] === 0x2d
  );
};

export const isValidMediaFile = async (file?: File | null) => {
  if (!file) return false;

  const fileType = file.type?.toLowerCase();
  const extension = file.name?.split(".").pop()?.toLowerCase();

  if (fileType === "image/heic" || extension === "heic") {
    return true;
  }

  const imageExtensions = ["png", "jpg", "jpeg", "gif", "webp"];
  const videoExtensions = ["mp4", "mov"];
  const pdfExtensions = ["pdf"];

  try {
    const signature = await readFileSignature(file);

    if (
      fileType?.includes("image") ||
      imageExtensions.includes(extension || "")
    ) {
      return isValidImageSignature(signature);
    }

    if (
      fileType?.includes("video") ||
      videoExtensions.includes(extension || "")
    ) {
      return isValidVideoSignature(signature);
    }

    if (
      fileType === "application/pdf" ||
      pdfExtensions.includes(extension || "")
    ) {
      return isValidPdfSignature(signature);
    }

    return false;
  } catch {
    return false;
  }
};

export const isValidImageFile = async (file?: File | null) => {
  if (!file) return false;

  const fileType = file.type?.toLowerCase();
  const extension = file.name?.split(".").pop()?.toLowerCase();

  if (fileType === "image/heic" || extension === "heic") {
    return true;
  }

  const imageExtensions = ["png", "jpg", "jpeg", "gif", "webp"];
  const supportedTypes = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/gif",
    "image/webp",
  ];

  if (
    !supportedTypes.includes(fileType || "") &&
    !imageExtensions.includes(extension || "")
  ) {
    return false;
  }

  try {
    const signature = await readFileSignature(file);
    return isValidImageSignature(signature);
  } catch {
    return false;
  }
};

export const isWrongFileType = (files: File[] | File, typeAllow?: string[]) => {
  const getFiles = files instanceof Array ? files : [files];
  const typeAllows: string[] = typeAllow || TYPE_ALLOW;

  const handleFileType = (fileName: string, fileType: string): string => {
    let modify = fileType;
    if (!fileType) {
      const getType = fileName.toLowerCase().split(".").pop();
      switch (getType) {
        case "csv":
          modify = "text/csv";
          break;
        case "heic":
          modify = "image/heic";
          break;
        default:
          break;
      }
    }
    return modify;
  };

  const isFileJFIF = (fileName: string) => {
    const getType = fileName.split(".").pop();
    return getType === "jfif";
  };
  return (
    !!getFiles.length &&
    getFiles.some(
      (file) =>
        isFileJFIF(file.name) ||
        !typeAllows.includes(handleFileType(file.name, file.type)),
    )
  );
};

export const isLongFileName = (files: File[] | File, maxLength: number) => {
  const getFiles = files instanceof Array ? files : [files];
  return Array.from(getFiles).some(
    (file) => file.name.split(".")[0].length > maxLength,
  );
};

export const toBase64 = (file: File) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export const multipleToBase64 = async (files: File[]) => {
  const promises = files.map(async (item: File) => {
    const url = await toBase64(item);
    (item as any).url = url;
    return item;
  });
  return Promise.all(promises);
};

export const isFileNameHasXss = (name: string) => {
  return /(<img).+(>)/g.test(name);
};

export const handleUploadStore = async (typePutFile: string, file: any) => {
  try {
    const hasThumbnail = file.type?.includes("video");
    setItem(StorageKey.NOT_SHOWN_POPUP, "true");
    const postUploadUrlsRes = await postUploadUrls({
      type: typePutFile,
      fileName: file?.name,
    });
    const { thumbnailSasUrl, sasUrl, filePath } = postUploadUrlsRes;
    setItem(StorageKey.MEDIA, {
      baseUrl: sasUrl,
      contentType: file.type,
    });
    await putUploadStore(file);
    if (hasThumbnail) {
      setItem(StorageKey.MEDIA, {
        baseUrl: thumbnailSasUrl,
        contentType: file.thumbnail.type,
      });
      await putUploadStore(file.thumbnail);
    }
    removeItem(StorageKey.MEDIA);
    const formatFile = Object.defineProperty(file, "filePath", {
      value: filePath,
      configurable: true,
    });
    return formatFile;
  } catch (error) {
    removeItem(StorageKey.MEDIA);
    return null;
  } finally {
    removeItem(StorageKey.NOT_SHOWN_POPUP);
  }
};
